import {
  EDITOR_LAYERING,
  EDITOR_MODE,
  useEditorBlocks,
  useEditorContent,
  useEditorDispatch,
  useEditorMode
} from "@pathwright/blocks-engine"
import Button from "@pathwright/ui/src/components/button/Button"
import { useTranslate } from "@pathwright/ui/src/components/lng/withTranslate"
import Pathicon from "@pathwright/ui/src/components/pathicon/Pathicon"
import View from "@pathwright/ui/src/components/ui/View"
import "element-closest"
import React from "react"
import { Motion, spring } from "react-motion"
import styled from "styled-components"
import PathwrightChakraTooltip from "../ui/PathwrightChakraTooltip"
import ContentPublishAction from "./ContentPublishAction"

const ControlsView = styled(View.Primary)`
  z-index: ${EDITOR_LAYERING.CONTENT_EDITOR_CONTROLS};
  position: ${(p) => (p.headerIsSticky ? "sticky" : "static")};
  top: 0;
  display: flex;
  align-items: baseline;
  justify-content: flex-end;
  width: 100%;
  padding: 10px;
  max-width: 1200px;
  opacity: 1;
`

const ControlsActions = styled.div`
  display: flex;
  align-items: center;

  .Button.Blocks_Mode {
    font-weight: bold;
    margin-right: 5px;
  }
`

const ContentEditorControls = () => {
  const { mode } = useEditorMode()
  const { isPublished } = useEditorContent()
  const { blocksAreEmpty } = useEditorBlocks()
  const { handleToggleMode: onToggleMode } = useEditorDispatch()

  const { tc } = useTranslate()

  const [headerIsSticky, setHeaderIsSticky] = React.useState(false)
  const stickyTriggerRef = React.useRef(null)

  React.useEffect(() => {
    if (!stickyTriggerRef.current) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.intersectionRatio === 0) {
          // Marker is completely OUT of viewport — header becomes sticky
          setHeaderIsSticky(true)
        } else {
          // Marker is visible — header stays static
          setHeaderIsSticky(false)
        }
      },
      {
        root: null, // observe relative to viewport
        threshold: [0] // trigger as soon as it leaves or enters
      }
    )

    observer.observe(stickyTriggerRef.current)

    return () => {
      observer.disconnect()
    }
  }, [stickyTriggerRef])

  const handleToggleMode = (e) => {
    // switch to edit/view mode
    onToggleMode()
  }

  return (
    <>
      <div
        // Used by the IntersectionObserver
        id="sticky-trigger-marker"
        style={{
          height: 1,
          marginTop: -1,
          pointerEvents: "none",
          visibility: "hidden"
        }}
        ref={stickyTriggerRef}
      />
      <ControlsView headerIsSticky={headerIsSticky}>
        <Motion
          defaultStyle={{ opacity: 0 }}
          style={{ opacity: blocksAreEmpty ? spring(0) : spring(1) }} // Animate the opacity based on state
          config={{ stiffness: 170, damping: 26 }} // Tweak animation speed (optional)
        >
          {(style) => (
            <ControlsActions
              className={`ControlsActions`}
              style={{
                opacity: style.opacity
                // transition: "opacity 0.3s ease"
              }}
            >
              <PathwrightChakraTooltip
                label={
                  mode === EDITOR_MODE.EDIT
                    ? tc("Preview drafted Blocks changes.")
                    : tc("Edit Blocks.")
                }
              >
                <Button
                  onClick={handleToggleMode}
                  styleType="tertiary"
                  className="ControlsActions__blocks-mode-button"
                  style={{
                    fontSize: "0.9em"
                  }}
                >
                  <Pathicon icon={mode === EDITOR_MODE.EDIT ? "eye" : "edit"} />
                  <span> </span>
                  {mode === EDITOR_MODE.EDIT ? tc("Preview") : tc("Edit")}
                </Button>
              </PathwrightChakraTooltip>
              <ContentPublishAction
                needsPublish={!isPublished && !blocksAreEmpty}
              />
            </ControlsActions>
          )}
        </Motion>
      </ControlsView>
    </>
  )
}

export default ContentEditorControls
